import type { ComparisonResult, QueryMode } from "../../../types";
import type { CompareResultView } from "../../../compareReadModel";

export interface BriefCitation {
  id: string;
  title: string;
  url?: string;
  source: string;
}

export interface BriefBrandResult {
  brandId: number;
  brandName: string;
  scores: Record<string, number | null>;
  averageScore: number | null;
  reasoning: string | null;
  isWinner: boolean;
  isAbsent: boolean;
}

export interface BriefFanOut {
  label: string;
  model: string;
  webSearch: boolean;
  responseId?: string;
  finishReason?: string | null;
  searchQueries?: string[];
}

export interface SearchActivity {
  phase: string;
  queries: string[];
  citedSourceCount: number;
}

export interface CompareResultBrief {
  id: number;
  title: string;
  subtitle: string;
  modelLabel: string;
  promptName: string;
  createdAt: string;
  status: string;
  queryMode: ComparisonResult["query_mode"];
  isSentiment: boolean;
  winner: string | null;
  verdict: string;
  summary: string | null;
  dimensions: string[];
  maxByDimension: Record<string, number>;
  brands: BriefBrandResult[];
  evidence: { id: string; label: string; body: string; kind: "reasoning" | "quote" | "mention" | "raw" }[];
  citations: BriefCitation[];
  fanOut: BriefFanOut[];
  searchActivity: SearchActivity[];
  rawResponse: string | null;
  metadata: ComparisonResult["llm_metadata"];
  analysisStatus: string | null;
  analysisError: string | null;
}

import { averageScore as scoreAverage, formatModelLabel as modelLabel } from "../../lib/format";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Single source of truth for whether a tracked brand was "not surfaced" in a
 * blind-query result: the model never mentioned it (visibility 0, no sentiment)
 * or it was explicitly marked "Not mentioned." Only meaningful for blind queries.
 */
export function isAbsent(
  queryMode: QueryMode,
  entry: { reasoning: string | null; scores: Record<string, number | null> },
): boolean {
  if (queryMode !== "blind_query") return false;
  return (
    entry.reasoning === "Not mentioned." ||
    ((entry.scores.visibility ?? 0) === 0 && entry.scores.sentiment === null)
  );
}

function briefTitle(winner: string | null) {
  return winner ? `${winner} leads the brief` : "Comparison research brief";
}

function verdictFor(winner: string | null, isSentiment: boolean) {
  return winner
    ? `${winner} ${isSentiment ? "was the strongest recommendation" : "scored highest overall"}.`
    : "No clear winner was identified in this result.";
}

function collectUrls(value: unknown, citations: BriefCitation[], source: string) {
  if (Array.isArray(value)) {
    value.forEach((item) => collectUrls(item, citations, source));
    return;
  }

  if (!isRecord(value)) return;

  const candidateUrl = typeof value.url === "string" ? value.url : typeof value.href === "string" ? value.href : undefined;
  const title = typeof value.title === "string" ? value.title : typeof value.name === "string" ? value.name : candidateUrl;
  if (candidateUrl && title && !citations.some((citation) => citation.url === candidateUrl)) {
    citations.push({ id: `${source}-${citations.length + 1}`, title, url: candidateUrl, source });
  }

  Object.values(value).forEach((nested) => collectUrls(nested, citations, source));
}

function callEntries(metadata: ComparisonResult["llm_metadata"]) {
  if (!metadata) return [];
  return [
    ["Generation", metadata.generation],
    ["Raw response", metadata.raw_generation],
    ["Grounding analysis", metadata.analysis],
  ] as const;
}

export function toCompareResultBrief(result: ComparisonResult): CompareResultBrief {
  const dimensions = Array.from(new Set(result.results.flatMap((entry) => Object.keys(entry.scores))));
  const maxByDimension: Record<string, number> = {};
  for (const dimension of dimensions) {
    maxByDimension[dimension] = Math.max(...result.results.map((entry) => entry.scores[dimension] ?? 0));
  }

  const isSentiment = result.prompt_type === "sentiment" || result.query_mode === "blind_query";
  const brands = result.results.map((entry) => ({
    brandId: entry.brand_id,
    brandName: entry.brand_name,
    scores: entry.scores,
    averageScore: scoreAverage(entry.scores),
    reasoning: entry.reasoning,
    isWinner: entry.brand_name === result.winner,
    isAbsent: isAbsent(result.query_mode, entry),
  }));

  const evidence: CompareResultBrief["evidence"] = [
    ...brands
      .filter((brand) => brand.reasoning && brand.reasoning !== "Not mentioned.")
      .map((brand) => ({
        id: `brand-${brand.brandId}`,
        label: brand.brandName,
        body: brand.reasoning!,
        kind: isSentiment ? ("quote" as const) : ("reasoning" as const),
      })),
    ...(result.mentions ?? [])
      .filter((mention) => Boolean(mention.context))
      .map((mention) => ({
        id: `mention-${mention.id}`,
        label: mention.brand_name,
        body: mention.context!,
        kind: "mention" as const,
      })),
  ];

  if (evidence.length === 0 && result.raw_response) {
    evidence.push({ id: "raw-response", label: "Model response", body: result.raw_response, kind: "raw" });
  }

  const citations: BriefCitation[] = [];
  for (const [label, call] of callEntries(result.llm_metadata)) {
    collectUrls(call?.annotations, citations, label);
    collectUrls(call?.tool_calls, citations, label);
  }

  const fanOut = callEntries(result.llm_metadata)
    .filter(([, call]) => Boolean(call))
    .map(([label, call]) => ({
      label,
      model: call!.model,
      webSearch: call!.requested_web_search,
      responseId: call!.response_id,
      finishReason: call!.finish_reason,
      searchQueries: call!.search_queries,
    }));

  const searchActivity: SearchActivity[] = callEntries(result.llm_metadata)
    .filter(([, call]) => call?.requested_web_search)
    .map(([label, call]) => {
      const phaseCitations: BriefCitation[] = [];
      collectUrls(call!.annotations, phaseCitations, label);
      collectUrls(call!.tool_calls, phaseCitations, label);
      return {
        phase: label,
        queries: call!.search_queries ?? [],
        citedSourceCount: phaseCitations.length,
      };
    });

  return {
    id: result.id,
    title: briefTitle(result.winner),
    subtitle: `${result.prompt_name} · ${modelLabel(result.model)}`,
    modelLabel: modelLabel(result.model),
    promptName: result.prompt_name,
    createdAt: result.created_at,
    status: result.status,
    queryMode: result.query_mode,
    isSentiment,
    winner: result.winner,
    verdict: verdictFor(result.winner, isSentiment),
    summary: result.summary,
    dimensions,
    maxByDimension,
    brands,
    evidence,
    citations,
    fanOut,
    searchActivity,
    rawResponse: result.raw_response,
    metadata: result.llm_metadata,
    analysisStatus: result.analysis_status,
    analysisError: result.analysis_error,
  };
}

export function toCompareResultBriefFromRich(view: CompareResultView): CompareResultBrief {
  const dimensions = Array.from(new Set(view.brands.flatMap((entry) => Object.keys(entry.scores))));
  const maxByDimension: Record<string, number> = {};
  for (const dimension of dimensions) {
    maxByDimension[dimension] = Math.max(...view.brands.map((entry) => entry.scores[dimension] ?? 0));
  }

  const isSentiment = view.prompt.type === "sentiment" || view.query_mode === "blind_query";
  const brands = view.brands.map((entry) => ({
    brandId: entry.brand_id,
    brandName: entry.brand_name,
    scores: entry.scores,
    averageScore: scoreAverage(entry.scores),
    reasoning: entry.reasoning,
    isWinner: entry.brand_name === view.result.winner,
    isAbsent: isAbsent(view.query_mode, entry),
  }));

  const evidence: CompareResultBrief["evidence"] = view.evidence
    .filter((item) => item.text && item.kind !== "summary")
    .map((item, index) => ({
      id: `${item.kind}-${item.brand_id ?? item.brand_name ?? index}`,
      label: item.label,
      body: item.text,
      kind: item.kind === "other_brand" ? ("mention" as const) : isSentiment ? ("quote" as const) : ("reasoning" as const),
    }));

  if (evidence.length === 0 && view.debug?.raw_response) {
    evidence.push({ id: "raw-response", label: "Model response", body: view.debug.raw_response, kind: "raw" });
  }

  const citations = view.citations.map((citation, index) => ({
    id: `${citation.source_phase}-${index + 1}`,
    title: citation.title ?? citation.url,
    url: citation.url,
    source: citation.source_phase,
  }));

  const fanOut = view.fanout.map((call) => ({
    label: call.phase === "raw_generation" ? "Raw response" : call.phase === "analysis" ? "Grounding analysis" : "Generation",
    model: call.model,
    webSearch: call.requested_web_search,
    responseId: call.response_id ?? undefined,
    finishReason: call.finish_reason,
    searchQueries: call.search_queries,
  }));

  const searchActivity: SearchActivity[] = view.fanout
    .filter((call) => call.requested_web_search)
    .map((call) => {
      const phase = call.phase === "raw_generation" ? "Raw response" : call.phase === "analysis" ? "Grounding analysis" : "Generation";
      const phaseCitations = view.citations.filter((c) => c.source_phase === call.phase);
      return {
        phase,
        queries: call.search_queries ?? [],
        citedSourceCount: phaseCitations.length,
      };
    });

  return {
    id: view.id,
    title: briefTitle(view.result.winner),
    subtitle: `${view.prompt.name} · ${modelLabel(view.model)}`,
    modelLabel: modelLabel(view.model),
    promptName: view.prompt.name,
    createdAt: view.created_at,
    status: view.status,
    queryMode: view.query_mode,
    isSentiment,
    winner: view.result.winner,
    verdict: verdictFor(view.result.winner, isSentiment),
    summary: view.result.summary,
    dimensions,
    maxByDimension,
    brands,
    evidence,
    citations,
    fanOut,
    searchActivity,
    rawResponse: view.debug?.raw_response ?? null,
    metadata: view.debug?.llm_metadata ?? null,
    analysisStatus: view.result.analysis_status,
    analysisError: view.result.analysis_error,
  };
}
